import { FC, useEffect, useMemo } from "react";
import { X, Folder } from "lucide-react";
import { Citation } from "@/constants/citation.constant";
import { SourceItem } from "../citation/sourceItem";

const getFolderPath = (source: Citation): string => {
  const raw = (source.file_path || source.source_file || "").trim();
  if (!raw) return "Other";
  const decoded = (() => {
    try { return decodeURIComponent(raw); } catch { return raw; }
  })();
  const lastSlash = decoded.lastIndexOf("/");
  return lastSlash > 0 ? decoded.slice(0, lastSlash) : "Root";
};

/**
 * Props for the SourcesModal component
 */
interface Props {
  open: boolean;
  sources: Citation[];
  onClose: () => void;
  onSourceClick?: (citation: Citation) => void;
  isCitationLoading: boolean;
  selectedCitationId: number | null;
}

/**
 * SourcesModal component displays a right-side sidebar with a list of citation sources.
 * Shows source count and allows clicking on sources to view details. Closes on Escape key.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Citation[]} props.sources - Array of citation sources
 * @param {() => void} props.onClose - Close callback
 * @param {(citation: Citation) => void} [props.onSourceClick] - Source click callback
 * @param {boolean} props.isCitationLoading - Whether citation is loading
 * @param {number | null} props.selectedCitationId - ID of selected citation
 * @returns {JSX.Element | null} Right-side sidebar with sources or null if not open
 * 
 * @example
 * ```tsx
 * <SourcesModal
 *   open={sourcesOpen}
 *   sources={citations}
 *   onClose={handleClose}
 *   onSourceClick={handleSourceClick}
 * />
 * ```
 */
export const SourcesModal: FC<Props> = ({ 
  open, 
  sources, 
  onClose, 
  onSourceClick,
  isCitationLoading,
  selectedCitationId
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const groupedSources = useMemo(() => {
    const groups = new Map<string, { source: Citation; index: number }[]>();
    sources.forEach((source, i) => {
      const folder = getFolderPath(source);
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder)!.push({ source, index: i + 1 });
    });
    return Array.from(groups.entries());
  }, [sources]);

  if (!open) return null;

  return (
    <aside
      className="w-full md:w-[400px] h-full bg-white dark:bg-[#0b0b0c] border-l border-gray-200 dark:border-gray-700 fixed md:relative inset-0 md:inset-auto z-50 md:z-auto flex flex-col"
      role="complementary"
      aria-label="Sources"
    >
      <div className="flex items-center justify-between px-4 py-2 flex-shrink-0 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center">
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            <span className="font-primary text-lg">Sources</span> <span className="text-sm font-medium text-zinc-500">
            | <span className="font-secondary font-medium text-zinc-500">Total sources : {sources.length}</span>
          </span>
          </div>
        </div>
        <button 
          aria-label="Close" 
          onClick={onClose} 
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          <X size={18} className="text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      <div className="px-4 py-3 flex-1 min-h-0 overflow-y-auto">
        {sources.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            No sources available
          </div>
        ) : (
          <div className="space-y-5">
            {groupedSources.map(([folder, items]) => {
              const segments = folder === "Root" || folder === "Other"
                ? [folder]
                : folder.split("/").filter(Boolean);
              return (
                <div key={folder} className="space-y-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {segments.map((seg, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${i * 12}px` }}
                      >
                        <Folder size={14} className="flex-shrink-0" />
                        <span className="break-all" title={seg}>{seg}</span>
                      </div>
                    ))}
                  </div>
                  <div
                    className="space-y-2 border-l border-gray-200 dark:border-gray-700 pl-4"
                    style={{ marginLeft: `${Math.max(segments.length - 1, 0) * 12}px` }}
                  >
                    {items.map(({ source, index }) => (
                      <SourceItem
                        key={source.id || index}
                        source={source}
                        index={index}
                        onClick={() => onSourceClick?.(source)}
                        isCitationLoading={isCitationLoading}
                        selectedCitationId={selectedCitationId}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};
